import { mergeAttributes, Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import type { CommandProps } from "@tiptap/core"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		tabs: {
			insertTabs: (titles?: string[]) => ReturnType
			addTab: (pos: number, title?: string) => ReturnType
			removeTab: (pos: number) => ReturnType
		}
	}
}

export const DEFAULT_TAB_TITLES = ["First tab", "Second tab"]

// Insertion leaves the caret after the block, which would open the last panel
// rather than the one the author is about to write in.
export function selectFirstPanel(
	{ tr, dispatch }: CommandProps,
	name: string,
	from: number,
): boolean {
	if (!dispatch) return true
	let target = -1
	tr.doc.descendants((node, pos) => {
		if (target >= 0) return false
		if (node.type.name === name && pos >= from - 1) {
			target = pos
			return false
		}
		return true
	})
	if (target < 0) return true
	tr.setSelection(TextSelection.near(tr.doc.resolve(target + 2)))
	return true
}

export const TabExtension = Node.create({
	name: "tab",
	content: "block+",
	defining: true,
	isolating: true,

	addAttributes() {
		return {
			title: {
				default: "Tab",
				parseHTML: (element: HTMLElement) =>
					element.getAttribute("data-title") ?? "Tab",
				renderHTML: (attributes: Record<string, unknown>) => ({
					"data-title": String(attributes.title ?? "Tab"),
				}),
			},
		}
	},

	parseHTML() {
		return [{ tag: 'div[data-type="tab"]' }]
	},

	renderHTML({ HTMLAttributes }) {
		// ProseMirror wants the content hole alone in its parent, hence the body.
		return [
			"div",
			mergeAttributes(HTMLAttributes, { "data-type": "tab" }),
			["div", { class: "docs-panel-body" }, 0],
		]
	},
})

export const TabsExtension = Node.create({
	name: "tabs",
	group: "block",
	content: "tab+",
	defining: true,
	isolating: true,

	parseHTML() {
		return [{ tag: 'div[data-type="tabs"]' }]
	},

	// `data-panel-group` and `data-active` carry the reader-side switching, so
	// the first tab shows before any script runs.
	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "tabs",
				"data-panel-group": "tabs",
				"data-active": "1",
			}),
			0,
		]
	},

	addCommands() {
		return {
			insertTabs:
				(titles = DEFAULT_TAB_TITLES) =>
				({ chain, state }) => {
					const from = state.selection.from
					return chain()
						.insertContent({
							type: this.name,
							content: titles.map((title) => ({
								type: "tab",
								attrs: { title },
								content: [{ type: "paragraph" }],
							})),
						})
						.command((props) => selectFirstPanel(props, this.name, from))
						.run()
				},

			addTab:
				(pos, title = "New tab") =>
				({ tr, state, dispatch }) => {
					const tabs = state.doc.nodeAt(pos)
					if (tabs?.type.name !== this.name) return false
					const tab = state.schema.nodes.tab?.createAndFill(
						{ title },
						state.schema.nodes.paragraph?.create(),
					)
					if (!tab) return false
					if (dispatch) tr.insert(pos + tabs.nodeSize - 1, tab)
					return true
				},

			removeTab:
				(pos) =>
				({ tr, state, dispatch }) => {
					const tab = state.doc.nodeAt(pos)
					if (tab?.type.name !== "tab") return false
					const parent = state.doc.resolve(pos).parent
					// A tabs block with one tab left has nothing to switch between.
					if (parent.type.name === this.name && parent.childCount <= 1) {
						return false
					}
					if (dispatch) tr.delete(pos, pos + tab.nodeSize)
					return true
				},
		}
	},
})
